import React, { Suspense, useEffect, useState } from 'react'
import type { DataNode } from 'antd/lib/tree'
import { Link } from 'react-router-dom'

import { useCategoriesListQuery } from 'features/categories/hooks'
import { type AdminUpdateCategorySchema, type CategorySchema, SoftwareAdminApi } from 'openapi'
import { useToast } from 'shadcdn/ui/use-toast'
import { Button } from 'ui/Button/Button'
import { CONFIG } from 'config'
import { CategoryRoutePath } from 'router'

interface ExtendedDataNode extends DataNode {
  key: string | number // key: DataNode.key but without 'bigint'
  softwareCount: number
  hasBranchSoftware: boolean
}

const Tree = React.lazy(() => import('antd/lib/tree'))

const TreePlaceholder: React.FC = () => <div>Loading Tree...</div>

export const AdminCategoriesPage: React.FC = () => {
  const { data: categories, isLoading: categoriesIsLoading, refetch: refetchCategories } = useCategoriesListQuery()
  const [treeData, setTreeData] = useState<DataNode[]>([])

  React.useEffect(() => {
    if (!categories) return
    setTreeData(transformCategoriesToTreeData(categories))
  }, [categories])

  const { toast } = useToast()

  const onDrop = (info: any) => {
    const dropKey = info.node.key
    const dragKey = info.dragNode.key
    const dropPos = info.node.pos.split('-')
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])

    const loop = (data: DataNode[], key: React.Key, callback: (nde: DataNode, i: number, data: DataNode[]) => void) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) return callback(data[i], i, data)

        if (data[i].children) {
          loop(data[i].children!, key, callback)
        }
      }
    }

    const data = [...treeData]

    // Find dragObject
    let dragObj: DataNode
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1)
      dragObj = item
    })

    if (!info.dropToGap) {
      // Drop on the content
      loop(data, dropKey, (item) => {
        item.children = item.children || []
        item.children.unshift(dragObj)
      })
    } else if (
      (info.node.props.children || []).length > 0 && // Has children
      info.node.props.expanded && // Is expanded
      dropPosition === 1 // On the bottom gap
    ) {
      loop(data, dropKey, (item) => {
        item.children = item.children || []
        item.children.unshift(dragObj)
      })
    } else {
      let ar: DataNode[] = []
      let i: number
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr
        i = index
      })
      if (dropPosition === -1) {
        ar.splice(i!, 0, dragObj!)
      } else {
        ar.splice(i! + 1, 0, dragObj!)
      }
    }

    setTreeData(data)
  }

  const handleSave = async () => {
    const flattenedCategories = flattenTreeForSave(treeData)
    try {
      await SoftwareAdminApi.updateCategory({ requestBody: flattenedCategories })
      toast({ title: 'Categories updated successfully' })
      refetchCategories()
    } catch (error) {
      toast({ title: 'Failed to update categories' })
      console.error('Failed to update categories:', error)
    }
  }

  const [expandedNodes, setExpandedNodes] = useState<React.Key[]>([])

  useEffect(() => {
    if (expandedNodes.length > 0) return
    setExpandedNodes((categories ?? []).map((c) => c.id.toString()))
  }, [categories, expandedNodes.length])

  if (categoriesIsLoading) return <div>Loading...</div>

  return (
    <div>
      <Suspense fallback={<TreePlaceholder />}>
        <Tree
          className="draggable-tree"
          draggable
          blockNode
          onDrop={onDrop}
          treeData={treeData}
          defaultExpandAll={true}
          onExpand={setExpandedNodes}
          expandedKeys={expandedNodes}
          titleRender={(nodeData) => {
            const extendedNodeData = nodeData as ExtendedDataNode
            return (
              <div className="flex flex-row gap-3">
                <div className="w-64">
                  {extendedNodeData.title?.toString()}
                  {extendedNodeData.hasBranchSoftware && <span className="ml-1">❌</span>}
                  {extendedNodeData.softwareCount > 0 && (
                    <span className="ml-1 text-gray-400">{extendedNodeData.softwareCount}</span>
                  )}
                </div>
                <a
                  className="text-xs underline text-blue-500"
                  href={`${CONFIG.API_BASE_URL}/admin/categories/category/${extendedNodeData.key}/change/`}
                >
                  Admin
                </a>
                <Link
                  className="text-xs underline text-blue-500"
                  to={CategoryRoutePath({ categoryId: extendedNodeData.key })}
                >
                  Catalogue
                </Link>
              </div>
            )
          }}
        />
      </Suspense>
      <Button onClick={handleSave} className="mt-2">
        Save Changes
      </Button>
    </div>
  )
}

const transformCategoriesToTreeData = (categories: CategorySchema[]): ExtendedDataNode[] => {
  const buildTree = (items: CategorySchema[], parentId: number | null = null): ExtendedDataNode[] => {
    return items
      .filter((item) => item.parent_id === parentId)
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        const children = buildTree(items, item.id)
        const { totalCount, hasBranchSoftware } = calculateSoftwareCount(item, children)
        return {
          key: item.id.toString(),
          title: item.name,
          children,
          softwareCount: totalCount,
          hasBranchSoftware,
        }
      })
  }

  return buildTree(categories)
}

const calculateSoftwareCount = (
  item: CategorySchema,
  children: ExtendedDataNode[],
): { totalCount: number; hasBranchSoftware: boolean } => {
  const branchCount = item.softwareCount || 0
  const childrenCount = children.reduce((sum, child) => sum + child.softwareCount, 0)
  const totalCount = branchCount + childrenCount
  const hasBranchSoftware = branchCount > 0 && children.length > 0

  return { totalCount, hasBranchSoftware }
}

const flattenTreeForSave = (nodes: DataNode[], _order = 0, parent: number | null = null): any[] => {
  let result: AdminUpdateCategorySchema[] = []
  nodes.forEach((node, index) => {
    result.push({
      id: Number.parseInt(node.key as string),
      name: node.title as string,
      order: index,
      parent_id: parent,
    })
    if (node.children) {
      result = result.concat(flattenTreeForSave(node.children, 0, Number.parseInt(node.key as string)))
    }
  })
  return result
}
